<script setup>
import { computed, reactive, ref, watch, onMounted } from 'vue';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { useStore } from 'vuex';

dayjs.extend(isBetween);
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import WorkforceAPI from 'dashboard/api/workforce';

import WorkforceButton from '../components/WorkforceButton.vue';
import WorkforceModal from '../components/WorkforceModal.vue';
import WorkforceScheduleCalendar from '../components/WorkforceScheduleCalendar.vue';
import WorkforceMonthlyCalendarModal from '../components/WorkforceMonthlyCalendarModal.vue';
import WorkforceSelect from '../components/WorkforceSelect.vue';

const store = useStore();
const { t } = useI18n();
const { currentAccount } = useAccount();
const agents = useMapGetter('agents/getAgents');

const schedules = ref([]);
const scheduleExceptions = ref([]);
const loading = ref(false);
const savingSchedule = ref(false);
const exceptionLoading = ref(false);
const exceptionSaving = ref(false);

const filters = reactive({
  userId: 'all',
  showInactive: true,
});

const selectedWeekStart = ref(dayjs().startOf('week').toISOString());

const selectedAgent = computed(() => {
  if (filters.userId === 'all') return null;
  return (
    agents.value.find(agent => String(agent.id) === String(filters.userId)) ||
    null
  );
});

const timezone = computed(() => currentAccount.value?.timezone || 'UTC');

const DAY_MINUTES = 24 * 60;
const MIN_GAP_MINUTES = 30;

const minutesToTime = minutes => {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) {
    return '--';
  }
  if (minutes >= DAY_MINUTES) {
    return '24:00';
  }
  return dayjs().startOf('day').add(minutes, 'minute').format('HH:mm');
};

const timeToMinutes = value => {
  if (value === null || value === undefined || value === '') return null;
  const [hoursPart, minutesPart] = String(value).split(':');
  const hours = Number(hoursPart);
  const minutes = Number(minutesPart || 0);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
};

const weekdayKeys = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
];

const weekdayIndexMap = weekdayKeys.reduce((acc, key, index) => {
  acc[key.toLowerCase()] = index;
  return acc;
}, {});

const resolveWeekdayIndex = value => {
  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    if (normalized.startsWith('workforce.weekdays.')) {
      const key = normalized.split('.').pop();
      return weekdayIndexMap[key] ?? null;
    }
    return weekdayIndexMap[normalized] ?? null;
  }
  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }
  if (value === null || value === undefined) return null;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

const weekdayLabel = index => {
  if (index === null || index === undefined || Number.isNaN(index)) {
    return '--';
  }
  const key = weekdayKeys[index];
  return key ? t(`WORKFORCE.WEEKDAYS.${key}`) : '--';
};

const agentNameById = id => {
  const agent = agents.value.find(item => Number(item.id) === Number(id));
  return agent ? agent.name : '';
};

const resolveWeekMonday = value => {
  const base = dayjs(value);
  return base.isValid() ? base.day(1) : dayjs().day(1);
};

const colorOptions = [
  {
    id: 'ink',
    label: 'Ink',
    bgClass: 'bg-gray-900',
    textClass: 'text-white',
    borderClass: 'border-gray-900/30',
    chipClass: 'bg-gray-900',
    bgHex: '#111827',
    textHex: '#ffffff',
    borderHex: '#111827',
  },
  {
    id: 'charcoal',
    label: 'Charcoal',
    bgClass: 'bg-slate-700',
    textClass: 'text-white',
    borderClass: 'border-slate-800',
    chipClass: 'bg-slate-700',
    bgHex: '#334155',
    textHex: '#f8fafc',
    borderHex: '#334155',
  },
  {
    id: 'steel',
    label: 'Steel',
    bgClass: 'bg-gray-600',
    textClass: 'text-white',
    borderClass: 'border-gray-700',
    chipClass: 'bg-gray-600',
    bgHex: '#4b5563',
    textHex: '#f9fafb',
    borderHex: '#4b5563',
  },
  {
    id: 'teal',
    label: 'Teal',
    bgClass: 'bg-teal-600',
    textClass: 'text-white',
    borderClass: 'border-teal-700',
    chipClass: 'bg-teal-600',
    bgHex: '#0d9488',
    textHex: '#ecfeff',
    borderHex: '#0f766e',
  },
  {
    id: 'aqua',
    label: 'Aqua',
    bgClass: 'bg-cyan-600',
    textClass: 'text-white',
    borderClass: 'border-cyan-700',
    chipClass: 'bg-cyan-600',
    bgHex: '#0891b2',
    textHex: '#ecfeff',
    borderHex: '#0e7490',
  },
  {
    id: 'mint',
    label: 'Mint',
    bgClass: 'bg-emerald-500',
    textClass: 'text-white',
    borderClass: 'border-emerald-600',
    chipClass: 'bg-emerald-500',
    bgHex: '#10b981',
    textHex: '#ecfdf5',
    borderHex: '#059669',
  },
  {
    id: 'forest',
    label: 'Forest',
    bgClass: 'bg-green-700',
    textClass: 'text-white',
    borderClass: 'border-green-800',
    chipClass: 'bg-green-700',
    bgHex: '#047857',
    textHex: '#d1fae5',
    borderHex: '#065f46',
  },
  {
    id: 'citrus',
    label: 'Citrus',
    bgClass: 'bg-lime-600',
    textClass: 'text-white',
    borderClass: 'border-lime-700',
    chipClass: 'bg-lime-600',
    bgHex: '#65a30d',
    textHex: '#f7fee7',
    borderHex: '#4d7c0f',
  },
  {
    id: 'sunrise',
    label: 'Sunrise',
    bgClass: 'bg-amber-500',
    textClass: 'text-white',
    borderClass: 'border-amber-600',
    chipClass: 'bg-amber-500',
    bgHex: '#f59e0b',
    textHex: '#fffbeb',
    borderHex: '#d97706',
  },
  {
    id: 'coral',
    label: 'Coral',
    bgClass: 'bg-orange-600',
    textClass: 'text-white',
    borderClass: 'border-orange-700',
    chipClass: 'bg-orange-600',
    bgHex: '#0058d8',
    textHex: '#fff7ed',
    borderHex: '#004db3',
  },
  {
    id: 'ruby',
    label: 'Ruby',
    bgClass: 'bg-rose-700',
    textClass: 'text-white',
    borderClass: 'border-rose-800',
    chipClass: 'bg-rose-700',
    bgHex: '#be123c',
    textHex: '#ffe4e6',
    borderHex: '#9f1239',
  },
  {
    id: 'blush',
    label: 'Blush',
    bgClass: 'bg-pink-600',
    textClass: 'text-white',
    borderClass: 'border-pink-700',
    chipClass: 'bg-pink-600',
    bgHex: '#db2777',
    textHex: '#fce7f3',
    borderHex: '#be185d',
  },
  {
    id: 'violet',
    label: 'Violet',
    bgClass: 'bg-violet-700',
    textClass: 'text-white',
    borderClass: 'border-violet-800',
    chipClass: 'bg-violet-700',
    bgHex: '#6d28d9',
    textHex: '#ede9fe',
    borderHex: '#5b21b6',
  },
  {
    id: 'indigo',
    label: 'Indigo',
    bgClass: 'bg-indigo-700',
    textClass: 'text-white',
    borderClass: 'border-indigo-800',
    chipClass: 'bg-indigo-700',
    bgHex: '#4338ca',
    textHex: '#e0e7ff',
    borderHex: '#3730a3',
  },
  {
    id: 'sky',
    label: 'Sky',
    bgClass: 'bg-sky-600',
    textClass: 'text-white',
    borderClass: 'border-sky-700',
    chipClass: 'bg-sky-600',
    bgHex: '#0284c7',
    textHex: '#e0f2fe',
    borderHex: '#0369a1',
  },
];

const agentColorOverrides = ref({});

const colorStorageKey = computed(() => {
  const accountId = currentAccount.value?.id;
  return accountId
    ? `workforce_agent_colors_${accountId}`
    : 'workforce_agent_colors_default';
});

const agentIndexMap = computed(() => {
  return agents.value.reduce((acc, agent, index) => {
    acc[Number(agent.id)] = index;
    return acc;
  }, {});
});

const defaultColorForAgent = agentId => {
  const index = agentIndexMap.value[Number(agentId)] ?? 0;
  return colorOptions[index % colorOptions.length];
};

const loadAgentColorOverrides = () => {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(colorStorageKey.value);
    agentColorOverrides.value = raw ? JSON.parse(raw) : {};
  } catch (error) {
    agentColorOverrides.value = {};
  }
};

const persistAgentColorOverrides = () => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      colorStorageKey.value,
      JSON.stringify(agentColorOverrides.value || {})
    );
  } catch (error) {
    // noop
  }
};

watch(colorStorageKey, () => {
  loadAgentColorOverrides();
});

watch(
  () => agentColorOverrides.value,
  () => {
    persistAgentColorOverrides();
  },
  { deep: true }
);

onMounted(() => {
  loadAgentColorOverrides();
});

const agentColorMap = computed(() => {
  return agents.value.reduce((acc, agent) => {
    const overrideId = agentColorOverrides.value?.[agent.id];
    const colorOption =
      colorOptions.find(option => option.id === overrideId) ||
      defaultColorForAgent(agent.id);
    acc[Number(agent.id)] = colorOption;
    return acc;
  }, {});
});

const mergeIntervals = intervals => {
  if (!intervals.length) return [];
  const sorted = [...intervals].sort((a, b) => a.start - b.start);
  const merged = [{ ...sorted[0] }];
  for (let index = 1; index < sorted.length; index += 1) {
    const current = sorted[index];
    const last = merged[merged.length - 1];
    if (current.start <= last.end) {
      last.end = Math.max(last.end, current.end);
    } else {
      merged.push({ ...current });
    }
  }
  return merged;
};

const buildCoverageMatrix = schedulesList => {
  const daily = Array.from({ length: 7 }, () => []);

  schedulesList.forEach(item => {
    const dayIdx = item.weekday_index;
    // 验证weekday_index是有效的数字且在0-6范围内
    if (
      dayIdx === null ||
      dayIdx === undefined ||
      dayIdx < 0 ||
      dayIdx > 6 ||
      Number.isNaN(dayIdx)
    )
      return;
    const start = item.startMinutes ?? timeToMinutes(item.start_time);
    const end = item.endMinutes ?? timeToMinutes(item.end_time);
    if (start === null || end === null) return;

    if (end > start) {
      daily[dayIdx].push({ start, end });
    } else {
      daily[dayIdx].push({ start, end: DAY_MINUTES });
      daily[(dayIdx + 1) % 7].push({ start: 0, end });
    }
  });

  return daily.map((intervals, dayIdx) => {
    const merged = mergeIntervals(intervals);
    let cursor = 0;
    const gaps = [];
    let covered = 0;

    merged.forEach(interval => {
      const boundedStart = Math.max(0, Math.min(DAY_MINUTES, interval.start));
      const boundedEnd = Math.max(0, Math.min(DAY_MINUTES, interval.end));
      if (boundedStart > cursor) {
        gaps.push({ start: cursor, end: boundedStart });
      }
      covered += Math.max(0, boundedEnd - boundedStart);
      cursor = Math.max(cursor, boundedEnd);
    });

    if (cursor < DAY_MINUTES) {
      gaps.push({ start: cursor, end: DAY_MINUTES });
    }

    const normalizedGaps = gaps.filter(gap => gap.end - gap.start > 0);
    const uncovered = normalizedGaps.reduce(
      (sum, gap) => sum + (gap.end - gap.start),
      0
    );

    return {
      dayIdx,
      intervals: merged,
      gaps: normalizedGaps,
      coveredMinutes: covered,
      uncoveredMinutes: uncovered,
    };
  });
};

const formatDurationMinutes = minutes => {
  if (!minutes) return '0m';
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours && remainder) return `${hours}h ${remainder}m`;
  if (hours) return `${hours}h`;
  return `${remainder}m`;
};

const defaultScheduleForm = () => ({
  id: null,
  user_id: filters.userId !== 'all' ? Number(filters.userId) : null,
  weekday: dayjs(selectedWeekStart.value).day(),
  start_time: '09:00',
  end_time: '10:00',
  timezone: timezone.value,
  buffer_minutes: 0,
  is_active: true,
  specificDate: null, // 如果设置，表示这是特定日期的schedule
  exceptionId: null, // 如果设置，表示这是编辑existing exception
});

const scheduleModal = reactive({
  open: false,
  isNew: true,
  isTemplate: false,
  form: defaultScheduleForm(),
});

const resetScheduleForm = () => {
  Object.assign(scheduleModal.form, defaultScheduleForm());
  scheduleModal.isNew = true;
  scheduleModal.isTemplate = false;
};

const defaultExceptionForm = () => ({
  id: null,
  user_id: filters.userId !== 'all' ? String(filters.userId) : '',
  date: dayjs(selectedWeekStart.value).format('YYYY-MM-DD'),
  start_time: '',
  end_time: '',
  status: 'time_off',
  timezone: timezone.value,
  reason: '',
  reason_error: '',
});

const exceptionModal = reactive({
  open: false,
  isNew: true,
  form: defaultExceptionForm(),
});

const resetExceptionForm = () => {
  Object.assign(exceptionModal.form, defaultExceptionForm());
  exceptionModal.isNew = true;
};

const colorDesignerModal = reactive({
  open: false,
  selections: {},
});

const weeklyPatternTemplate = reactive({
  schedules: [],
  lastModified: null,
});

const weeklyPatternStorageKey = computed(() => {
  const id = currentAccount.value?.id;
  return id ? `workforce_weekly_pattern_${id}` : null;
});

const generateTemplateScheduleId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `tpl-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const loadWeeklyPatternTemplate = () => {
  weeklyPatternTemplate.schedules = [];
  weeklyPatternTemplate.lastModified = null;

  if (typeof window === 'undefined') return;
  const storageKey = weeklyPatternStorageKey.value;
  if (!storageKey) return;

  try {
    const saved = window.localStorage.getItem(storageKey);
    if (!saved) return;
    const parsed = JSON.parse(saved);
    weeklyPatternTemplate.schedules = Array.isArray(parsed?.schedules)
      ? parsed.schedules
      : [];
    weeklyPatternTemplate.lastModified = parsed?.lastModified || null;
  } catch (error) {
    weeklyPatternTemplate.schedules = [];
    weeklyPatternTemplate.lastModified = null;
  }
};

const saveWeeklyPatternTemplate = () => {
  if (typeof window === 'undefined') return;
  const storageKey = weeklyPatternStorageKey.value;
  if (!storageKey) return;

  weeklyPatternTemplate.lastModified = new Date().toISOString();
  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        schedules: weeklyPatternTemplate.schedules,
        lastModified: weeklyPatternTemplate.lastModified,
      })
    );
  } catch (error) {
    // noop
  }
};

watch(
  () => weeklyPatternStorageKey.value,
  () => {
    loadWeeklyPatternTemplate();
  },
  { immediate: true }
);

const monthlyViewModal = ref(false);

const openMonthlyView = () => {
  monthlyViewModal.value = true;
};

const closeMonthlyView = () => {
  monthlyViewModal.value = false;
};

const agentColorId = agentId => {
  const value = agentColorOverrides.value?.[agentId];
  if (value) return value;
  return defaultColorForAgent(agentId).id;
};

const openColorDesigner = () => {
  const initialSelections = agents.value.reduce((acc, agent) => {
    acc[agent.id] = agentColorId(agent.id);
    return acc;
  }, {});
  colorDesignerModal.selections = initialSelections;
  colorDesignerModal.open = true;
};

const closeColorDesigner = () => {
  colorDesignerModal.open = false;
};

const resetColorDesignerSelections = () => {
  colorDesignerModal.selections = agents.value.reduce((acc, agent) => {
    acc[agent.id] = defaultColorForAgent(agent.id).id;
    return acc;
  }, {});
};

const submitColorDesigner = () => {
  const draft = {};
  Object.entries(colorDesignerModal.selections || {}).forEach(([id, value]) => {
    if (value) {
      draft[id] = value;
    }
  });
  agentColorOverrides.value = draft;
  colorDesignerModal.open = false;
};

const dayCoverageModal = reactive({
  open: false,
  loading: false,
  form: {
    weekday: dayjs().day(),
    date: dayjs().format('YYYY-MM-DD'),
    start_time: '09:00',
    end_time: '18:00',
    agentIds: [],
    replaceExisting: false,
    dateLabel: '',
    isTemplate: false,
  },
});

const resetDayCoverageForm = (weekday = dayjs().day(), isoDate = null) => {
  const startOfWeek = dayjs(selectedWeekStart.value).startOf('day');
  const date = isoDate ? dayjs(isoDate) : startOfWeek.add(weekday, 'day');
  dayCoverageModal.form.weekday = weekday;
  dayCoverageModal.form.date = date.format('YYYY-MM-DD');
  dayCoverageModal.form.start_time = '09:00';
  dayCoverageModal.form.end_time = '18:00';
  dayCoverageModal.form.agentIds = [];
  dayCoverageModal.form.replaceExisting = false;
  dayCoverageModal.form.dateLabel = date.format('MMM D, YYYY');
  dayCoverageModal.form.isTemplate = false;
};

const openDayCoverageModal = (weekday = dayjs().day(), isoDate = null) => {
  resetDayCoverageForm(weekday, isoDate);
  dayCoverageModal.open = true;
};

const openTemplateDayCoverageModal = (
  weekday = dayjs().day(),
  isoDate = null
) => {
  resetDayCoverageForm(weekday, isoDate);
  dayCoverageModal.form.isTemplate = true;
  dayCoverageModal.open = true;
};

const closeDayCoverageModal = () => {
  dayCoverageModal.open = false;
};

watch(
  () => dayCoverageModal.form.date,
  newDate => {
    if (!newDate) return;
    const parsed = dayjs(newDate);
    if (!parsed.isValid()) return;
    const weekday = parsed.day();
    if (dayCoverageModal.form.weekday !== weekday) {
      dayCoverageModal.form.weekday = weekday;
    }
    dayCoverageModal.form.dateLabel = parsed.format('MMM D, YYYY');
    if (!dayCoverageModal.form.isTemplate) {
      selectedWeekStart.value = parsed.startOf('week').toISOString();
    }
  }
);

watch(
  () => dayCoverageModal.form.weekday,
  newWeekday => {
    if (newWeekday === null || newWeekday === undefined) return;
    const base = dayjs(selectedWeekStart.value)
      .startOf('week')
      .add(Number(newWeekday), 'day');
    if (!base.isValid()) return;
    const formatted = base.format('YYYY-MM-DD');
    if (dayCoverageModal.form.date !== formatted) {
      dayCoverageModal.form.date = formatted;
      return;
    }
    dayCoverageModal.form.dateLabel = base.format('MMM D, YYYY');
  }
);

const loadSchedules = async () => {
  loading.value = true;
  try {
    const params = {};
    if (filters.userId !== 'all') params.user_id = filters.userId;
    const response = await WorkforceAPI.getSchedules(params);
    schedules.value = response.data || [];
  } catch (error) {
    useAlert(t('WORKFORCE.TOASTS.SCHEDULE_FETCH_ERROR'));
  } finally {
    loading.value = false;
  }
};

const loadExceptions = async () => {
  exceptionLoading.value = true;
  try {
    const params = {};
    if (filters.userId !== 'all') {
      params.user_id = filters.userId;
    }
    const response = await WorkforceAPI.getScheduleExceptions(params);
    scheduleExceptions.value = response.data || [];
  } catch (error) {
    useAlert(t('WORKFORCE.TOASTS.EXCEPTION_FETCH_ERROR'));
  } finally {
    exceptionLoading.value = false;
  }
};

const submitDayCoverageForm = async () => {
  const selectedAgents = (dayCoverageModal.form.agentIds || [])
    .map(agentId => Number(agentId))
    .filter(id => !Number.isNaN(id));

  if (!selectedAgents.length) {
    useAlert(t('WORKFORCE.SCHEDULES.TOASTS.BULK_DAY_AGENTS'));
    return;
  }

  const startMinutes = timeToMinutes(dayCoverageModal.form.start_time);
  const endMinutes = timeToMinutes(dayCoverageModal.form.end_time);
  if (
    startMinutes === null ||
    endMinutes === null ||
    startMinutes === endMinutes
  ) {
    useAlert(t('WORKFORCE.SCHEDULES.TOASTS.INVALID_TIME_RANGE'));
    return;
  }

  const weekday = Number(dayCoverageModal.form.weekday);
  const startTime = dayCoverageModal.form.start_time;
  const endTime = dayCoverageModal.form.end_time;

  if (dayCoverageModal.form.isTemplate) {
    if (dayCoverageModal.form.replaceExisting) {
      weeklyPatternTemplate.schedules = weeklyPatternTemplate.schedules.filter(
        schedule => Number(schedule.weekday_index) !== weekday
      );
    } else {
      weeklyPatternTemplate.schedules = weeklyPatternTemplate.schedules.filter(
        schedule =>
          !(
            Number(schedule.weekday_index) === weekday &&
            selectedAgents.includes(Number(schedule.user_id))
          )
      );
    }

    selectedAgents.forEach(agentId => {
      weeklyPatternTemplate.schedules.push({
        id: generateTemplateScheduleId(),
        user_id: Number(agentId),
        weekday_index: weekday,
        start_time: startTime,
        end_time: endTime,
        timezone: timezone.value,
        buffer_minutes: 0,
        is_active: true,
        agent_name: agentNameById(agentId) || '',
      });
    });

    saveWeeklyPatternTemplate();
    dayCoverageModal.open = false;
    useAlert(t('WORKFORCE.SCHEDULES.TOASTS.BULK_DAY_SUCCESS'));
    return;
  }

  dayCoverageModal.loading = true;
  const targetDate = dayCoverageModal.form.date;

  try {
    // 删除该日期该agent的现有exceptions（如果选择replace）
    if (dayCoverageModal.form.replaceExisting) {
      const existingExceptions = scheduleExceptions.value.filter(
        exc =>
          exc.date === targetDate &&
          selectedAgents.includes(Number(exc.user_id))
      );
      await Promise.all(
        existingExceptions.map(exc =>
          WorkforceAPI.deleteScheduleException(exc.id)
        )
      );
    }

    // 为每个agent创建override exception
    await Promise.all(
      selectedAgents.map(agentId =>
        WorkforceAPI.createScheduleException({
          user_id: agentId,
          date: targetDate,
          start_time: startTime,
          end_time: endTime,
          status: 'override',
          timezone: timezone.value,
          reason: '',
        })
      )
    );

    useAlert(t('WORKFORCE.SCHEDULES.TOASTS.BULK_DAY_SUCCESS'));
    dayCoverageModal.open = false;
    await loadExceptions();
  } catch (error) {
    useAlert(t('WORKFORCE.SCHEDULES.TOASTS.BULK_DAY_ERROR'));
  } finally {
    dayCoverageModal.loading = false;
  }
};

const normalizedSchedules = computed(() =>
  schedules.value.map(item => {
    // 尝试从weekday, weekday_value, weekday_index等多个字段获取weekday信息
    const weekdaySource =
      item.weekday ?? item.weekday_value ?? item.weekday_index;
    const weekdayIndex = resolveWeekdayIndex(weekdaySource);
    const startMinutes = item.start_time
      ? timeToMinutes(item.start_time)
      : null;
    const endMinutes = item.end_time ? timeToMinutes(item.end_time) : null;
    let durationMinutes = 0;
    if (startMinutes !== null && endMinutes !== null) {
      if (endMinutes > startMinutes) {
        durationMinutes = endMinutes - startMinutes;
      } else if (endMinutes !== startMinutes) {
        durationMinutes = DAY_MINUTES - startMinutes + endMinutes;
      } else {
        durationMinutes = DAY_MINUTES;
      }
    }
    return {
      ...item,
      agent_name: agentNameById(item.user_id),
      weekday_index: weekdayIndex,
      startMinutes,
      endMinutes,
      durationMinutes,
    };
  })
);

const visibleSchedules = computed(() =>
  normalizedSchedules.value
    .filter(item =>
      filters.userId === 'all'
        ? true
        : String(item.user_id) === String(filters.userId)
    )
    .filter(item => (filters.showInactive ? true : item.is_active))
);

const calendarSchedules = computed(() =>
  visibleSchedules.value
    .filter(item => item.weekday_index !== null)
    .map(item => ({
      ...item,
      color:
        agentColorMap.value[Number(item.user_id)] ||
        defaultColorForAgent(item.user_id),
    }))
);

const calendarExceptions = computed(() =>
  scheduleExceptions.value.map(item => ({
    ...item,
    weekday_index: dayjs(item.date).day(),
    agent_name: agentNameById(item.user_id),
  }))
);

const tableSchedules = computed(() =>
  visibleSchedules.value
    .map(item => ({
      ...item,
      agentName: item.agent_name,
      dayLabel: weekdayLabel(item.weekday_index),
      startLabel: item.start_time?.slice(0, 5),
      endLabel: item.end_time?.slice(0, 5),
    }))
    .sort((a, b) => {
      const weekdayA = a.weekday_index ?? 7;
      const weekdayB = b.weekday_index ?? 7;
      if (weekdayA !== weekdayB) return weekdayA - weekdayB;
      const startA = a.startMinutes ?? 0;
      const startB = b.startMinutes ?? 0;
      return startA - startB;
    })
);

const activeSchedulesForMetrics = computed(() => {
  // 用于coverage分析的schedules：全部active的recurring schedules + 当前周的override/overtime exceptions
  // 注意：不应用userId过滤，而是显示全部team的覆盖情况
  const weekStart = dayjs(selectedWeekStart.value).startOf('week');
  const weekEnd = weekStart.add(6, 'day').endOf('day');

  // 获取当前周的exceptions并转换为schedule格式（只包括override和overtime，不包括time_off）
  const weekExceptions = scheduleExceptions.value
    .filter(exc => {
      const excDate = dayjs(exc.date);
      if (!excDate.isBetween(weekStart, weekEnd, null, '[]')) return false;
      // 只包括override和overtime状态的exceptions（排除time_off）
      if (exc.status === 'time_off') return false;
      return true;
    })
    .map(exc => ({
      user_id: exc.user_id,
      weekday_index: dayjs(exc.date).day(),
      start_time: exc.start_time,
      end_time: exc.end_time,
      startMinutes: timeToMinutes(exc.start_time),
      endMinutes: timeToMinutes(exc.end_time),
      is_active: true,
      agent_name: agentNameById(exc.user_id),
    }));

  // 获取所有active的recurring schedules（用于coverage分析，不应用userId过滤）
  const recurringSchedules = normalizedSchedules.value.filter(item => {
    // 只过滤is_active，不应用userId过滤
    if (!item.is_active) return false;
    return true;
  });

  return [...recurringSchedules, ...weekExceptions];
});

const coverageAnalysis = computed(() => {
  const matrix = buildCoverageMatrix(activeSchedulesForMetrics.value);
  const totalCovered = matrix.reduce((sum, day) => sum + day.coveredMinutes, 0);
  const totalUncovered = matrix.reduce(
    (sum, day) => sum + day.uncoveredMinutes,
    0
  );
  const allGaps = matrix.flatMap(day =>
    day.gaps.map(gap => ({
      weekdayIndex: day.dayIdx,
      start: gap.start,
      end: gap.end,
      duration: gap.end - gap.start,
    }))
  );
  const significantGaps = allGaps
    .filter(gap => gap.duration >= MIN_GAP_MINUTES)
    .sort((a, b) => b.duration - a.duration);
  const longestGap = significantGaps.length ? significantGaps[0] : null;
  const coveragePercentage = ((totalCovered / (DAY_MINUTES * 7)) * 100).toFixed(
    1
  );

  return {
    daily: matrix,
    totalCovered,
    totalUncovered,
    coveragePercentage,
    gaps: significantGaps,
    longestGap,
  };
});

const coverageSummary = computed(() => {
  const { totalUncovered, coveragePercentage, gaps, longestGap } =
    coverageAnalysis.value;
  return {
    uncoveredHours: (totalUncovered / 60).toFixed(1),
    coveragePercentage,
    gapCount: gaps.length,
    longestGap,
  };
});

const coverageHighlights = computed(() => {
  // 按天分组gaps，确保每一天至少显示一个最大的gap
  const gapsByDay = {};
  coverageAnalysis.value.gaps.forEach(gap => {
    const dayIdx = gap.weekdayIndex;
    if (!gapsByDay[dayIdx]) {
      gapsByDay[dayIdx] = [];
    }
    gapsByDay[dayIdx].push(gap);
  });

  // 对每一天的gaps按duration排序，取每天最大的gap
  const dailyMaxGaps = Object.entries(gapsByDay).map(([dayIdx, gaps]) => {
    const sorted = gaps.sort((a, b) => b.duration - a.duration);
    return { ...sorted[0], dayIdx: Number(dayIdx) };
  });

  // 按duration从大到小排序
  const sorted = dailyMaxGaps.sort((a, b) => b.duration - a.duration);

  // 显示所有天的最大gap（最多7天，一周7天）
  return sorted.slice(0, 7).map(gap => ({
    label: `${weekdayLabel(gap.weekdayIndex)} ${minutesToTime(gap.start)} – ${minutesToTime(gap.end)}`,
    durationLabel: formatDurationMinutes(gap.duration),
  }));
});

const coverageLongestLabel = computed(() => {
  const { longestGap } = coverageAnalysis.value;
  if (!longestGap) return '--';
  return `${weekdayLabel(longestGap.weekdayIndex)} ${minutesToTime(longestGap.start)} – ${minutesToTime(longestGap.end)}`;
});

const agentsWithoutSchedule = computed(() => {
  const activeAgentIds = new Set(
    normalizedSchedules.value
      .filter(item => item.is_active)
      .map(item => Number(item.user_id))
  );
  return agents.value.filter(agent => !activeAgentIds.has(Number(agent.id)));
});

const agentsWithoutSchedulePreview = computed(() => {
  if (!agentsWithoutSchedule.value.length) return '';
  const names = agentsWithoutSchedule.value
    .slice(0, 3)
    .map(agent => agent.name)
    .filter(Boolean);
  const remaining = agentsWithoutSchedule.value.length - names.length;
  if (!names.length) return '';
  if (remaining > 0) {
    return `${names.join(', ')} +${remaining}`;
  }
  return names.join(', ');
});

const weeklyPlannerDays = computed(() => {
  // 显示selectedWeekStart这一周的7天（周日到周六）
  const weekStart = dayjs(selectedWeekStart.value).startOf('week');

  return Array.from({ length: 7 }, (_, index) => {
    const date = weekStart.add(index, 'day');
    const weekdayIndex = date.day();
    const isoDate = date.format('YYYY-MM-DD');

    // 优先显示exceptions（特定日期的override），如果没有则显示recurring schedules
    const exceptionsForDate = scheduleExceptions.value.filter(
      exc => exc.date === isoDate
    );

    let schedulesForDay = [];

    if (exceptionsForDate.length > 0) {
      // 有exceptions，显示exceptions
      schedulesForDay = exceptionsForDate
        .filter(exc =>
          filters.userId === 'all'
            ? true
            : String(exc.user_id) === String(filters.userId)
        )
        .map(exc => {
          const agent = agents.value.find(
            a => Number(a.id) === Number(exc.user_id)
          );
          return {
            id: exc.id,
            agentName: agent?.name || `User ${exc.user_id}`,
            startLabel: exc.start_time?.slice(0, 5) || '00:00',
            endLabel: exc.end_time?.slice(0, 5) || '23:59',
            isActive: true,
            color:
              agentColorMap.value[Number(exc.user_id)] ||
              defaultColorForAgent(exc.user_id),
            raw: exc,
            isException: true,
          };
        });
    } else {
      // 没有exceptions，显示recurring schedules
      schedulesForDay = calendarSchedules.value
        .filter(schedule => schedule.weekday_index === weekdayIndex)
        .map(schedule => ({
          id: schedule.id,
          agentName: schedule.agent_name,
          startLabel:
            schedule.start_time?.slice(0, 5) ||
            minutesToTime(schedule.startMinutes ?? 0),
          endLabel:
            schedule.end_time?.slice(0, 5) ||
            minutesToTime(schedule.endMinutes ?? 0),
          isActive: schedule.is_active,
          color:
            agentColorMap.value[Number(schedule.user_id)] ||
            defaultColorForAgent(schedule.user_id),
          raw: schedule,
          isException: false,
        }));
    }

    return {
      index: weekdayIndex,
      label: weekdayLabel(weekdayIndex),
      dateLabel: date.format('MMM D'),
      fullDateLabel: date.format('MMM D, YYYY'),
      isoDate: isoDate,
      actualDate: date,
      schedules: schedulesForDay,
    };
  });
});

const weeklyPatternTemplateDays = computed(() => {
  return Array.from({ length: 7 }, (_, weekdayIndex) => {
    const date = dayjs().startOf('week').add(weekdayIndex, 'day');
    const schedulesForDay = (weeklyPatternTemplate.schedules || []).filter(
      schedule => Number(schedule.weekday_index) === weekdayIndex
    );
    return {
      index: weekdayIndex,
      label: weekdayLabel(weekdayIndex),
      dateLabel: date.format('MMM D'),
      fullDateLabel: date.format('MMM D, YYYY'),
      isoDate: date.toISOString(),
      actualDate: date,
      schedules: schedulesForDay.map(schedule => ({
        id: schedule.id,
        agentName: agentNameById(schedule.user_id) || schedule.agent_name || '',
        startLabel: schedule.start_time,
        endLabel: schedule.end_time,
        isActive: schedule.is_active,
        color:
          agentColorMap.value[Number(schedule.user_id)] ||
          defaultColorForAgent(schedule.user_id),
        raw: schedule,
      })),
    };
  });
});

const openScheduleModalForDay = (weekday, isoDate) => {
  // Weekday coverage planner现在创建specific date exception
  const date =
    isoDate ||
    dayjs(selectedWeekStart.value)
      .startOf('week')
      .add(weekday, 'day')
      .format('YYYY-MM-DD');
  openExceptionModalForDate(weekday, date);
};

const openExceptionModalForDate = (weekday, isoDate) => {
  resetScheduleForm();
  scheduleModal.form.user_id =
    filters.userId !== 'all' ? Number(filters.userId) : null;
  scheduleModal.form.weekday = weekday;
  scheduleModal.form.start_time = '09:00';
  scheduleModal.form.end_time = '18:00';
  scheduleModal.form.timezone = timezone.value;
  scheduleModal.form.buffer_minutes = 0;
  scheduleModal.form.is_active = true;
  scheduleModal.form.specificDate = isoDate; // 标记这是特定日期的schedule
  scheduleModal.isTemplate = false;
  scheduleModal.open = true;
};

const openTemplateScheduleModal = (weekday, schedule = null) => {
  resetScheduleForm();
  scheduleModal.isTemplate = true;
  scheduleModal.form.weekday = weekday;
  scheduleModal.form.start_time = schedule?.start_time || '09:00';
  scheduleModal.form.end_time = schedule?.end_time || '18:00';
  scheduleModal.form.user_id =
    schedule?.user_id !== undefined
      ? Number(schedule.user_id)
      : filters.userId !== 'all'
        ? Number(filters.userId)
        : null;
  scheduleModal.form.timezone = schedule?.timezone || timezone.value;
  scheduleModal.form.buffer_minutes = schedule?.buffer_minutes || 0;
  scheduleModal.form.is_active = schedule?.is_active ?? true;
  scheduleModal.form.id = schedule?.id ?? null;
  scheduleModal.isNew = !schedule;
  scheduleModal.open = true;
};

const selectedAgentCoverage = computed(() => {
  if (!selectedAgent.value) return null;
  const agentId = Number(selectedAgent.value.id);
  const agentSchedules = normalizedSchedules.value.filter(
    item =>
      Number(item.user_id) === agentId &&
      (filters.showInactive ? true : item.is_active)
  );

  if (!agentSchedules.length) {
    return {
      totalHours: '0.0',
      offDays: 7,
      daysScheduled: 0,
      timeOffCount: 0,
      nextShift: null,
      gaps: [],
    };
  }

  const matrix = buildCoverageMatrix(agentSchedules);
  const totalMinutes = matrix.reduce((sum, day) => sum + day.coveredMinutes, 0);
  const offDays = matrix.filter(day => day.coveredMinutes === 0).length;
  const gapList = matrix.flatMap(day =>
    day.gaps.map(gap => ({
      weekdayIndex: day.dayIdx,
      start: gap.start,
      end: gap.end,
      duration: gap.end - gap.start,
    }))
  );

  const weekStart = dayjs(selectedWeekStart.value).startOf('day');
  const weekEnd = weekStart.add(6, 'day').endOf('day');
  const timeOffCount = scheduleExceptions.value.filter(item => {
    if (item.status !== 'time_off') return false;
    const date = dayjs(item.date);
    return date.isBetween(weekStart, weekEnd, null, '[]');
  }).length;

  const upcomingShift =
    agentSchedules
      .filter(item => item.is_active)
      .sort((a, b) => {
        const weekdayDiff = (a.weekday_index ?? 7) - (b.weekday_index ?? 7);
        if (weekdayDiff !== 0) return weekdayDiff;
        return (a.startMinutes ?? 0) - (b.startMinutes ?? 0);
      })[0] || null;

  return {
    totalHours: (totalMinutes / 60).toFixed(1),
    offDays,
    daysScheduled: 7 - offDays,
    timeOffCount,
    nextShift: upcomingShift
      ? {
          dayLabel: weekdayLabel(upcomingShift.weekday_index),
          start:
            upcomingShift.start_time?.slice(0, 5) ||
            minutesToTime(upcomingShift.startMinutes ?? 0),
          end:
            upcomingShift.end_time?.slice(0, 5) ||
            minutesToTime(upcomingShift.endMinutes ?? 0),
        }
      : null,
    gaps: gapList
      .filter(gap => gap.duration >= MIN_GAP_MINUTES)
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 3)
      .map(gap => ({
        label: `${weekdayLabel(gap.weekdayIndex)} ${minutesToTime(gap.start)} – ${minutesToTime(gap.end)}`,
        durationLabel: formatDurationMinutes(gap.duration),
      })),
  };
});

const exceptionList = computed(() =>
  scheduleExceptions.value.map(item => ({
    ...item,
    dateLabel: dayjs(item.date).format('MMM D, YYYY'),
    timeLabel: item.start_time
      ? `${item.start_time.slice(0, 5)} - ${
          item.end_time?.slice(0, 5) || '24:00'
        }`
      : t('WORKFORCE.EXCEPTIONS.ALL_DAY'),
  }))
);

const weekLabel = computed(() => {
  const start = dayjs(selectedWeekStart.value);
  const end = start.add(6, 'day');
  return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
});

const goToWeek = delta => {
  selectedWeekStart.value = dayjs(selectedWeekStart.value)
    .add(delta, 'week')
    .toISOString();
};

const goToToday = () => {
  selectedWeekStart.value = dayjs().startOf('week').toISOString();
};

const openScheduleModal = (schedule = null) => {
  resetScheduleForm();
  if (schedule) {
    scheduleModal.isNew = false;
    Object.assign(scheduleModal.form, {
      id: schedule.id,
      user_id: Number(schedule.user_id),
      weekday:
        resolveWeekdayIndex(schedule.weekday ?? schedule.weekday_value) ?? 0,
      start_time: schedule.start_time?.slice(0, 5) || '09:00',
      end_time: schedule.end_time?.slice(0, 5) || '10:00',
      timezone: schedule.timezone || timezone.value,
      buffer_minutes: schedule.buffer_minutes ?? 0,
      is_active: schedule.is_active,
    });
  }
  scheduleModal.open = true;
};

const handleCalendarCreate = ({ weekday, startMinutes, date }) => {
  resetScheduleForm();
  scheduleModal.form.weekday = weekday;
  scheduleModal.form.specificDate = date; // 设置特定日期
  if (startMinutes !== undefined) {
    scheduleModal.form.start_time = minutesToTime(startMinutes);
    scheduleModal.form.end_time = minutesToTime(
      Math.min(startMinutes + 60, 23 * 60 + 30)
    );
  } else {
    scheduleModal.form.start_time = '09:00';
    scheduleModal.form.end_time = '18:00';
  }
  scheduleModal.open = true;
};

const handleMonthlyAssignCoverage = ({ weekday, date }) => {
  openDayCoverageModal(weekday, date);
};

const handleEditException = exception => {
  // 将exception当作schedule编辑（用户看到的是schedule表单）
  resetScheduleForm();
  scheduleModal.isNew = false;
  scheduleModal.form.id = exception.id;
  scheduleModal.form.user_id = Number(exception.user_id);
  scheduleModal.form.weekday = dayjs(exception.date).day();
  scheduleModal.form.start_time = exception.start_time?.slice(0, 5) || '09:00';
  scheduleModal.form.end_time = exception.end_time?.slice(0, 5) || '18:00';
  scheduleModal.form.timezone = exception.timezone || timezone.value;
  scheduleModal.form.buffer_minutes = 0;
  scheduleModal.form.is_active = true;
  scheduleModal.form.specificDate = exception.date;
  scheduleModal.form.exceptionId = exception.id; // 保存exception id用于更新
  scheduleModal.open = true;
};

const submitSchedule = async () => {
  if (!scheduleModal.form.user_id) {
    useAlert(t('WORKFORCE.TOASTS.SELECT_AGENT_WARNING'));
    return;
  }

  if (scheduleModal.isTemplate) {
    const templateSchedule = {
      id: scheduleModal.isNew
        ? generateTemplateScheduleId()
        : scheduleModal.form.id,
      user_id: Number(scheduleModal.form.user_id),
      weekday_index: Number(scheduleModal.form.weekday),
      start_time: scheduleModal.form.start_time,
      end_time: scheduleModal.form.end_time,
      timezone: scheduleModal.form.timezone || timezone.value,
      buffer_minutes: Number(scheduleModal.form.buffer_minutes || 0),
      is_active: !!scheduleModal.form.is_active,
      agent_name: agentNameById(scheduleModal.form.user_id) || '',
    };

    if (scheduleModal.isNew) {
      weeklyPatternTemplate.schedules.push(templateSchedule);
      useAlert(t('WORKFORCE.TOASTS.SCHEDULE_CREATED'));
    } else {
      const index = weeklyPatternTemplate.schedules.findIndex(
        item => item.id === scheduleModal.form.id
      );
      if (index > -1) {
        weeklyPatternTemplate.schedules.splice(index, 1, templateSchedule);
      } else {
        weeklyPatternTemplate.schedules.push(templateSchedule);
      }
      useAlert(t('WORKFORCE.TOASTS.SCHEDULE_UPDATED'));
    }

    saveWeeklyPatternTemplate();
    scheduleModal.open = false;
    return;
  }

  savingSchedule.value = true;

  // 如果是特定日期的schedule，创建/更新为exception（但UI显示为schedule）
  if (scheduleModal.form.specificDate) {
    const userId = Number(scheduleModal.form.user_id);
    const targetDate = scheduleModal.form.specificDate;
    const startMinutes = timeToMinutes(scheduleModal.form.start_time);
    const endMinutes = timeToMinutes(scheduleModal.form.end_time);

    // 检查时间冲突（排除正在编辑的exception）
    const conflicts = scheduleExceptions.value.filter(exc => {
      if (exc.id === scheduleModal.form.exceptionId) return false; // 排除自己
      if (Number(exc.user_id) !== userId) return false;
      if (exc.date !== targetDate) return false;

      const excStart = timeToMinutes(exc.start_time);
      const excEnd = timeToMinutes(exc.end_time);

      // 检查时间是否重叠
      return startMinutes < excEnd && endMinutes > excStart;
    });

    if (conflicts.length > 0) {
      useAlert(t('WORKFORCE.TOASTS.TIME_CONFLICT'));
      savingSchedule.value = false;
      return;
    }

    const exceptionPayload = {
      user_id: userId,
      date: targetDate,
      start_time: scheduleModal.form.start_time,
      end_time: scheduleModal.form.end_time,
      status: 'override',
      timezone: scheduleModal.form.timezone || timezone.value,
      reason: '',
    };

    try {
      if (scheduleModal.form.exceptionId) {
        // 更新现有exception
        await WorkforceAPI.updateScheduleException(
          scheduleModal.form.exceptionId,
          exceptionPayload
        );
        useAlert(t('WORKFORCE.TOASTS.SCHEDULE_UPDATED'));
      } else {
        // 创建新exception
        await WorkforceAPI.createScheduleException(exceptionPayload);
        useAlert(t('WORKFORCE.TOASTS.SCHEDULE_CREATED'));
      }
      scheduleModal.open = false;
      await loadExceptions();
    } catch (error) {
      useAlert(t('WORKFORCE.TOASTS.SCHEDULE_SAVE_ERROR'));
    } finally {
      savingSchedule.value = false;
    }
    return;
  }

  // 否则创建recurring schedule
  const payload = {
    user_id: Number(scheduleModal.form.user_id),
    weekday: Number(scheduleModal.form.weekday),
    start_time: scheduleModal.form.start_time,
    end_time: scheduleModal.form.end_time,
    timezone: scheduleModal.form.timezone || timezone.value,
    buffer_minutes: Number(scheduleModal.form.buffer_minutes || 0),
    is_active: !!scheduleModal.form.is_active,
  };
  try {
    if (scheduleModal.isNew) {
      await WorkforceAPI.createSchedule(payload);
      useAlert(t('WORKFORCE.TOASTS.SCHEDULE_CREATED'));
    } else {
      await WorkforceAPI.updateSchedule(scheduleModal.form.id, payload);
      useAlert(t('WORKFORCE.TOASTS.SCHEDULE_UPDATED'));
    }
    scheduleModal.open = false;
    await loadSchedules();
  } catch (error) {
    useAlert(t('WORKFORCE.TOASTS.SCHEDULE_SAVE_ERROR'));
  } finally {
    savingSchedule.value = false;
  }
};

const deleteSchedule = async schedule => {
  try {
    await WorkforceAPI.deleteSchedule(schedule.id);
    useAlert(t('WORKFORCE.TOASTS.SCHEDULE_DELETED'));
    await loadSchedules();
  } catch (error) {
    useAlert(t('WORKFORCE.TOASTS.SCHEDULE_DELETE_ERROR'));
  }
};

const deleteTemplateSchedule = schedule => {
  if (!schedule) return;
  if (
    typeof window !== 'undefined' &&
    !window.confirm(t('WORKFORCE.FORMS.DELETE_CONFIRM'))
  ) {
    return;
  }
  const index = weeklyPatternTemplate.schedules.findIndex(
    item => item.id === schedule.id
  );
  if (index > -1) {
    weeklyPatternTemplate.schedules.splice(index, 1);
    saveWeeklyPatternTemplate();
    useAlert(t('WORKFORCE.TOASTS.SCHEDULE_DELETED'));
  }
};

const openExceptionModal = (exception = null) => {
  if (!exception && filters.userId === 'all') {
    useAlert(t('WORKFORCE.EXCEPTIONS.SELECT_AGENT_HINT'));
    return;
  }
  resetExceptionForm();
  if (exception) {
    exceptionModal.isNew = false;
    Object.assign(exceptionModal.form, {
      id: exception.id,
      user_id: String(exception.user_id),
      date: exception.date,
      start_time: exception.start_time?.slice(0, 5) || '',
      end_time: exception.end_time?.slice(0, 5) || '',
      status: exception.status,
      timezone: exception.timezone || timezone.value,
      reason: exception.reason || '',
    });
  }
  exceptionModal.open = true;
};

const submitExceptionForm = async () => {
  exceptionModal.form.reason_error = '';
  if (!exceptionModal.form.user_id || !exceptionModal.form.date) {
    useAlert(t('WORKFORCE.EXCEPTIONS.TOASTS.MISSING_REQUIRED'));
    return;
  }
  if (
    exceptionModal.form.start_time &&
    exceptionModal.form.end_time &&
    exceptionModal.form.end_time <= exceptionModal.form.start_time
  ) {
    exceptionModal.form.reason_error = t(
      'WORKFORCE.EXCEPTIONS.VALIDATION.TIME_RANGE'
    );
    return;
  }
  exceptionSaving.value = true;
  const payload = {
    user_id: Number(exceptionModal.form.user_id),
    date: exceptionModal.form.date,
    start_time: exceptionModal.form.start_time || null,
    end_time: exceptionModal.form.end_time || null,
    status: exceptionModal.form.status,
    timezone: exceptionModal.form.timezone || timezone.value,
    reason: exceptionModal.form.reason,
  };
  try {
    if (exceptionModal.isNew) {
      await WorkforceAPI.createScheduleException(payload);
      useAlert(t('WORKFORCE.EXCEPTIONS.TOASTS.CREATED'));
    } else {
      await WorkforceAPI.updateScheduleException(
        exceptionModal.form.id,
        payload
      );
      useAlert(t('WORKFORCE.EXCEPTIONS.TOASTS.UPDATED'));
    }
    exceptionModal.open = false;
    await loadExceptions();
  } catch (error) {
    useAlert(t('WORKFORCE.EXCEPTIONS.TOASTS.SAVE_ERROR'));
  } finally {
    exceptionSaving.value = false;
  }
};

const deleteException = async exception => {
  try {
    await WorkforceAPI.deleteScheduleException(exception.id);
    useAlert(t('WORKFORCE.TOASTS.EXCEPTION_DELETED'));
    await loadExceptions();
  } catch (error) {
    useAlert(t('WORKFORCE.TOASTS.EXCEPTION_DELETE_ERROR'));
  }
};

watch(
  () => filters.userId,
  async () => {
    await loadSchedules();
    await loadExceptions();
    resetScheduleForm();
    resetExceptionForm();
  }
);

watch(
  () => selectedWeekStart.value,
  () => {
    resetScheduleForm();
    resetExceptionForm();
  }
);

onMounted(async () => {
  if (!agents.value.length) {
    await store.dispatch('agents/get');
  }
  await loadSchedules();
  await loadExceptions();
});

const weeklyPatternModal = ref(false);

const openWeeklyPatternModal = () => {
  loadWeeklyPatternTemplate();
  weeklyPatternModal.value = true;
};

const closeWeeklyPatternModal = () => {
  weeklyPatternModal.value = false;
};

const replacingWeekKey = ref(null);

const handleReplaceWeek = async payload => {
  const weekId = payload?.weekId;
  const days = payload?.days || [];
  if (!weekId || !days.length) {
    useAlert(t('WORKFORCE.SCHEDULES.TOASTS.REPLACE_WEEK_ERROR'));
    return;
  }

  const dateByWeekday = days.reduce((acc, day) => {
    acc[day.weekday] = day.isoDate;
    return acc;
  }, {});

  const weekStartDate = days[0].isoDate;
  const weekEndDate = days[days.length - 1].isoDate;

  loadWeeklyPatternTemplate();
  const templateSchedules = weeklyPatternTemplate.schedules || [];

  console.log('[Weekly Pattern] Template loaded:', {
    total: templateSchedules.length,
    schedules: templateSchedules,
  });

  const schedulesToApply = templateSchedules.filter(item =>
    filters.userId === 'all'
      ? true
      : String(item.user_id) === String(filters.userId)
  );

  if (!schedulesToApply.length) {
    useAlert(t('WORKFORCE.SCHEDULES.TOASTS.REPLACE_WEEK_EMPTY'));
    console.log('[Weekly Pattern] No schedules to apply');
    return;
  }

  console.log('[Weekly Pattern] Schedules to apply:', schedulesToApply);

  const targetUserIds = new Set(
    schedulesToApply.map(item => Number(item.user_id))
  );

  replacingWeekKey.value = weekId;

  try {
    const response = await WorkforceAPI.getScheduleExceptions({
      from: weekStartDate,
      to: weekEndDate,
    });
    const existing = response.data || [];
    const deletions = existing
      .filter(exception => targetUserIds.has(Number(exception.user_id)))
      .map(exception => WorkforceAPI.deleteScheduleException(exception.id));

    if (deletions.length) {
      await Promise.all(deletions);
    }

    const timezoneValue = timezone.value;
    const creations = schedulesToApply
      .map(schedule => {
        const weekdayIndex = Number(schedule.weekday_index);
        const date = dateByWeekday[weekdayIndex];
        if (!date) return null;
        const candidateStart = schedule.start_time;
        const candidateEnd = schedule.end_time;
        if (!candidateStart || !candidateEnd) {
          return null;
        }
        return WorkforceAPI.createScheduleException({
          user_id: Number(schedule.user_id),
          date,
          start_time: candidateStart,
          end_time: candidateEnd,
          status: 'override',
          timezone: schedule.timezone || timezoneValue,
          metadata: { source: 'weekly_pattern_template' },
        });
      })
      .filter(Boolean);

    if (creations.length) {
      console.log('[Weekly Pattern] Creating exceptions:', creations);
      await Promise.all(creations);
    } else {
      console.log('[Weekly Pattern] No creations generated');
    }

    useAlert(t('WORKFORCE.SCHEDULES.TOASTS.REPLACE_WEEK_SUCCESS'));
    await loadSchedules();
    await loadExceptions();
  } catch (error) {
    useAlert(t('WORKFORCE.SCHEDULES.TOASTS.REPLACE_WEEK_ERROR'));
  } finally {
    replacingWeekKey.value = null;
  }
};
</script>

<template>
  <section class="flex min-h-full flex-col gap-6 px-8 pb-12 pt-5 lg:px-10">
    <div
      class="rounded-xl border border-n-weak bg-n-solid-2 px-5 py-4 shadow-sm"
    >
      <div
        class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
      >
        <div class="flex flex-wrap items-center gap-3">
          <label
            class="grid gap-1 text-xs uppercase tracking-wide text-n-slate-10"
          >
            {{ t('WORKFORCE.FILTERS.AGENT') }}
            <WorkforceSelect
              v-model="filters.userId"
              class="mt-1 min-w-[220px]"
            >
              <option value="all">
                {{ t('WORKFORCE.FILTERS.ALL_AGENTS') }}
              </option>
              <option
                v-for="agent in agents"
                :key="agent.id"
                :value="String(agent.id)"
              >
                {{ agent.name }}
              </option>
            </WorkforceSelect>
          </label>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <WorkforceButton
            size="xs"
            :variant="filters.showInactive ? 'ghost' : 'solid'"
            icon="i-lucide-filter"
            @click="filters.showInactive = !filters.showInactive"
          >
            {{
              filters.showInactive
                ? t('WORKFORCE.SCHEDULES.TOGGLES.HIDE_INACTIVE')
                : t('WORKFORCE.SCHEDULES.TOGGLES.SHOW_INACTIVE')
            }}
          </WorkforceButton>
          <WorkforceButton
            size="xs"
            variant="ghost"
            icon="i-lucide-palette"
            @click="openColorDesigner"
          >
            {{ t('WORKFORCE.SCHEDULES.ACTIONS.MANAGE_COLORS') }}
          </WorkforceButton>
          <WorkforceButton
            size="xs"
            variant="ghost"
            icon="i-lucide-calendar-range"
            @click="openWeeklyPatternModal"
          >
            {{ t('WORKFORCE.SCHEDULES.ACTIONS.BULK_WEEK') }}
          </WorkforceButton>
          <WorkforceButton
            size="xs"
            variant="ghost"
            icon="i-lucide-users"
            @click="openDayCoverageModal()"
          >
            {{ t('WORKFORCE.SCHEDULES.ACTIONS.BULK_DAY') }}
          </WorkforceButton>
          <WorkforceButton
            variant="ghost"
            size="xs"
            icon="i-lucide-chevrons-left"
            :aria-label="t('WORKFORCE.SCHEDULES.PREVIOUS_WEEK')"
            @click="goToWeek(-1)"
          />
          <span
            class="min-w-[150px] text-center text-sm font-semibold text-n-slate-12"
          >
            {{ weekLabel }}
          </span>
          <WorkforceButton
            variant="ghost"
            size="xs"
            icon="i-lucide-chevrons-right"
            :aria-label="t('WORKFORCE.SCHEDULES.NEXT_WEEK')"
            @click="goToWeek(1)"
          />
          <WorkforceButton variant="ghost" size="xs" @click="goToToday">
            {{ t('WORKFORCE.SCHEDULES.GO_TODAY') }}
          </WorkforceButton>
          <WorkforceButton
            size="xs"
            color="slate"
            icon="i-lucide-calendar-plus"
            @click="openScheduleModal()"
          >
            {{ t('WORKFORCE.ACTIONS.ADD_SCHEDULE') }}
          </WorkforceButton>
        </div>
      </div>
    </div>

    <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      <div
        class="relative overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 p-4 shadow-sm"
      >
        <div class="absolute left-0 top-0 h-full w-1 rounded-l-xl bg-ruby-9" />
        <p
          class="text-[11px] font-medium uppercase tracking-wide text-n-slate-9"
        >
          {{ t('WORKFORCE.SCHEDULES.SUMMARY.UNCOVERED_HOURS') }}
        </p>
        <p class="mt-2 text-2xl font-bold text-n-slate-12">
          {{ coverageSummary.uncoveredHours }}
          <span class="text-sm font-medium text-n-slate-10">h</span>
        </p>
        <p class="mt-1 text-[11px] text-n-slate-9">
          {{
            t('WORKFORCE.SCHEDULES.SUMMARY.UNCOVERED_HINT', {
              count: coverageSummary.gapCount,
            })
          }}
        </p>
        <p class="text-[11px] text-n-slate-9">
          {{
            t('WORKFORCE.SCHEDULES.SUMMARY.LONGEST_GAP', {
              label: coverageLongestLabel,
            })
          }}
        </p>
      </div>

      <div
        class="relative overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 p-4 shadow-sm"
      >
        <div class="absolute left-0 top-0 h-full w-1 rounded-l-xl bg-teal-9" />
        <p
          class="text-[11px] font-medium uppercase tracking-wide text-n-slate-9"
        >
          {{ t('WORKFORCE.SCHEDULES.SUMMARY.COVERAGE_PERCENT') }}
        </p>
        <p class="mt-2 text-2xl font-bold text-n-slate-12">
          {{ coverageAnalysis.coveragePercentage }}
          <span class="text-sm font-medium text-n-slate-10">%</span>
        </p>
        <p class="mt-1 text-[11px] text-n-slate-9">
          {{ t('WORKFORCE.SCHEDULES.SUMMARY.COVERAGE_HINT') }}
        </p>
      </div>

      <div
        class="relative overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 p-4 shadow-sm"
      >
        <div class="absolute left-0 top-0 h-full w-1 rounded-l-xl bg-amber-9" />
        <p
          class="text-[11px] font-medium uppercase tracking-wide text-n-slate-9"
        >
          {{ t('WORKFORCE.SCHEDULES.SUMMARY.UNASSIGNED_AGENTS') }}
        </p>
        <p class="mt-2 text-2xl font-bold text-n-slate-12">
          {{ agentsWithoutSchedule.length }}
        </p>
        <p
          v-if="agentsWithoutSchedule.length"
          class="mt-1 text-[11px] text-n-slate-9"
        >
          {{ t('WORKFORCE.SCHEDULES.SUMMARY.UNASSIGNED_HINT') }}
        </p>
        <p v-else class="mt-1 text-[11px] text-n-slate-9">
          {{ t('WORKFORCE.SCHEDULES.SUMMARY.UNASSIGNED_EMPTY') }}
        </p>
        <p
          v-if="agentsWithoutSchedulePreview"
          class="text-[11px] text-n-slate-9"
        >
          {{ agentsWithoutSchedulePreview }}
        </p>
      </div>

      <div
        class="relative overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 p-4 shadow-sm"
      >
        <div
          class="absolute left-0 top-0 h-full w-1 rounded-l-xl bg-indigo-9"
        />
        <p
          class="text-[11px] font-medium uppercase tracking-wide text-n-slate-9"
        >
          {{ t('WORKFORCE.SCHEDULES.SUMMARY.SELECTED_AGENT_TITLE') }}
        </p>
        <template v-if="selectedAgent && selectedAgentCoverage">
          <p class="mt-2 text-sm font-semibold text-n-slate-12">
            {{ selectedAgent.name }}
          </p>
          <p class="text-xs text-n-slate-9">
            {{
              t('WORKFORCE.SCHEDULES.SUMMARY.SELECTED_AGENT_METRICS', {
                hours: selectedAgentCoverage.totalHours,
                days: selectedAgentCoverage.daysScheduled,
                off: selectedAgentCoverage.offDays,
              })
            }}
          </p>
          <p class="mt-1 text-[11px] text-n-slate-9">
            <span v-if="selectedAgentCoverage.nextShift">
              {{
                t('WORKFORCE.SCHEDULES.SUMMARY.SELECTED_AGENT_NEXT_SHIFT', {
                  label: selectedAgentCoverage.nextShift.dayLabel,
                  start: selectedAgentCoverage.nextShift.start,
                  end: selectedAgentCoverage.nextShift.end,
                })
              }}
            </span>
            <span v-else>
              {{ t('WORKFORCE.SCHEDULES.SUMMARY.SELECTED_AGENT_NO_SHIFT') }}
            </span>
          </p>
          <p class="mt-1 text-[11px] text-n-slate-9">
            {{
              t('WORKFORCE.SCHEDULES.SUMMARY.SELECTED_AGENT_TIME_OFF', {
                count: selectedAgentCoverage.timeOffCount,
              })
            }}
          </p>
          <p
            v-if="selectedAgentCoverage.gaps.length"
            class="mt-1 text-[11px] text-n-slate-9"
          >
            {{
              t('WORKFORCE.SCHEDULES.SUMMARY.SELECTED_AGENT_GAP', {
                label: selectedAgentCoverage.gaps[0].label,
                duration: selectedAgentCoverage.gaps[0].durationLabel,
              })
            }}
          </p>
        </template>
        <p v-else class="mt-2 text-xs text-n-slate-9">
          {{ t('WORKFORCE.SCHEDULES.SUMMARY.SELECTED_AGENT_PLACEHOLDER') }}
        </p>
      </div>
    </div>

    <div
      class="rounded-xl border border-n-weak bg-n-solid-2 px-5 py-4 shadow-sm"
    >
      <div
        class="flex flex-col gap-1 md:flex-row md:items-center md:justify-between"
      >
        <div>
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('WORKFORCE.SCHEDULES.WEEKLY.TITLE') }}
          </h3>
          <p class="mt-0.5 text-xs text-n-slate-10">
            {{ weekLabel }}
          </p>
        </div>
        <div class="flex items-center gap-3">
          <WorkforceButton
            size="xs"
            color="slate"
            icon="i-lucide-calendar-days"
            @click="openMonthlyView"
          >
            {{ t('WORKFORCE.SCHEDULES.MONTHLY_VIEW.OPEN_BUTTON') }}
          </WorkforceButton>
        </div>
      </div>
      <div class="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <div
          v-for="day in weeklyPlannerDays"
          :key="day.index"
          class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-3 p-4 shadow-sm"
        >
          <div class="flex items-center justify-between gap-2">
            <div>
              <p class="text-xs uppercase tracking-wide text-n-slate-10">
                {{ day.label }}
              </p>
              <p class="text-sm font-semibold text-n-slate-12">
                {{ day.dateLabel }}
              </p>
            </div>
            <div class="flex flex-wrap justify-end gap-2">
              <WorkforceButton
                size="xs"
                variant="ghost"
                color="onyx"
                icon="i-lucide-plus"
                @click="openScheduleModalForDay(day.index, day.isoDate)"
              >
                {{ t('WORKFORCE.SCHEDULES.WEEKLY.ADD_SLOT') }}
              </WorkforceButton>
              <WorkforceButton
                size="xs"
                variant="ghost"
                color="onyx"
                icon="i-lucide-users"
                @click="openDayCoverageModal(day.index, day.isoDate)"
              >
                {{ t('WORKFORCE.SCHEDULES.WEEKLY.ASSIGN_DAY') }}
              </WorkforceButton>
            </div>
          </div>
          <div class="grid gap-2">
            <p v-if="!day.schedules.length" class="text-xs text-n-slate-9">
              {{ t('WORKFORCE.SCHEDULES.WEEKLY.EMPTY') }}
            </p>
            <ul v-else class="grid gap-2">
              <li
                v-for="schedule in day.schedules"
                :key="schedule.id"
                class="group flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-xs transition hover:border-n-strong hover:bg-n-solid-1"
                @click="
                  schedule.isException
                    ? handleEditException(schedule.raw)
                    : openScheduleModal(schedule.raw)
                "
              >
                <div class="flex items-center gap-2">
                  <span
                    class="inline-flex h-2.5 w-2.5 rounded-full"
                    :class="schedule.color?.chipClass || 'bg-black'"
                    :style="
                      schedule.color
                        ? { backgroundColor: schedule.color.bgHex }
                        : null
                    "
                  />
                  <div class="flex flex-col">
                    <span class="font-semibold">
                      {{
                        schedule.agentName ||
                        t('WORKFORCE.LABELS.UNKNOWN_AGENT')
                      }}
                    </span>
                    <span class="text-[11px] text-n-slate-9">
                      {{ schedule.startLabel }} – {{ schedule.endLabel }}
                    </span>
                  </div>
                </div>
                <div class="flex items-center gap-2">
                  <span
                    v-if="!schedule.isActive"
                    class="rounded-full bg-amber-3/50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-11"
                  >
                    {{ t('WORKFORCE.TABLE.STATUS_INACTIVE') }}
                  </span>
                  <WorkforceButton
                    size="xs"
                    variant="ghost"
                    icon="i-lucide-trash"
                    class="opacity-0 transition group-hover:opacity-100"
                    @click.stop="
                      schedule.isException
                        ? deleteException(schedule.raw)
                        : deleteSchedule(schedule.raw)
                    "
                  >
                    {{ t('WORKFORCE.ACTIONS.DELETE') }}
                  </WorkforceButton>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm">
      <div
        class="flex items-center justify-between border-b border-n-weak/60 px-5 py-3"
      >
        <div>
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('WORKFORCE.SCHEDULES.COVERAGE.GAPS_TITLE') }}
          </h3>
          <p class="mt-0.5 text-xs text-n-slate-10">{{ weekLabel }}</p>
        </div>
        <span class="text-xs text-n-slate-10">
          {{
            t('WORKFORCE.SCHEDULES.COVERAGE.GAPS_SUBTITLE', {
              count: coverageHighlights.length,
            })
          }}
        </span>
      </div>
      <div class="px-5 py-4">
        <p v-if="!coverageHighlights.length" class="text-sm text-n-slate-10">
          {{ t('WORKFORCE.SCHEDULES.COVERAGE.NO_GAPS') }}
        </p>
        <ul v-else class="grid gap-2 text-sm text-n-slate-12">
          <li
            v-for="item in coverageHighlights"
            :key="item.label"
            class="flex items-center justify-between rounded-lg border border-n-weak bg-n-solid-3 px-4 py-2"
          >
            <span>{{ item.label }}</span>
            <span
              class="rounded-full bg-ruby-3/30 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-ruby-11"
            >
              {{ item.durationLabel }}
            </span>
          </li>
        </ul>
      </div>
    </div>

    <div class="relative">
      <div
        v-if="loading"
        class="absolute inset-0 z-10 flex items-center justify-center bg-n-solid-1/60 text-sm text-n-slate-10"
      >
        {{ t('WORKFORCE.TABLE.LOADING') }}
      </div>
      <WorkforceScheduleCalendar
        :week-start="selectedWeekStart"
        :schedules="calendarSchedules"
        :exceptions="calendarExceptions"
        :agent-colors="agentColorMap"
        @create:schedule="handleCalendarCreate"
        @edit:schedule="openScheduleModal"
        @edit:exception="handleEditException"
      />
    </div>

    <div class="grid gap-4 lg:grid-cols-2">
      <div class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm">
        <div
          class="flex items-center justify-between border-b border-n-weak/60 px-5 py-3"
        >
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('WORKFORCE.TABLE.SCHEDULES_TITLE') }}
          </h3>
          <span class="text-xs text-n-slate-10">
            {{
              t('WORKFORCE.TABLE.SCHEDULE_COUNT', {
                count: tableSchedules.length,
              })
            }}
          </span>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-n-weak text-sm">
            <thead
              class="bg-n-solid-2 text-left text-xs uppercase tracking-wide text-n-slate-10"
            >
              <tr>
                <th class="px-5 py-3">{{ t('WORKFORCE.TABLE.AGENT') }}</th>
                <th class="px-5 py-3">{{ t('WORKFORCE.TABLE.DAY') }}</th>
                <th class="px-5 py-3">{{ t('WORKFORCE.TABLE.START') }}</th>
                <th class="px-5 py-3">{{ t('WORKFORCE.TABLE.END') }}</th>
                <th class="px-5 py-3 text-right">
                  {{ t('WORKFORCE.TABLE.ACTIONS') }}
                </th>
              </tr>
            </thead>
            <tbody
              class="divide-y divide-n-weak/70 bg-n-solid-3 text-n-slate-12"
            >
              <tr v-if="!tableSchedules.length">
                <td
                  colspan="5"
                  class="px-5 py-6 text-center text-sm text-n-slate-10"
                >
                  {{ t('WORKFORCE.TABLE.EMPTY') }}
                </td>
              </tr>
              <tr v-for="item in tableSchedules" :key="item.id">
                <td class="px-5 py-3 font-medium">{{ item.agentName }}</td>
                <td class="px-5 py-3">{{ item.dayLabel }}</td>
                <td class="px-5 py-3">{{ item.startLabel }}</td>
                <td class="px-5 py-3">{{ item.endLabel }}</td>
                <td class="px-5 py-3 text-right">
                  <div class="flex justify-end gap-2">
                    <WorkforceButton
                      variant="ghost"
                      size="xs"
                      icon="i-lucide-pencil"
                      @click="openScheduleModal(item)"
                    >
                      {{ t('WORKFORCE.ACTIONS.EDIT') }}
                    </WorkforceButton>
                    <WorkforceButton
                      variant="ghost"
                      size="xs"
                      icon="i-lucide-trash"
                      @click="deleteSchedule(item)"
                    >
                      {{ t('WORKFORCE.ACTIONS.DELETE') }}
                    </WorkforceButton>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm">
        <div
          class="flex items-center justify-between border-b border-n-weak/60 px-5 py-3"
        >
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('WORKFORCE.EXCEPTIONS.TITLE') }}
          </h3>
          <WorkforceButton
            size="xs"
            icon="i-lucide-plus"
            :disabled="filters.userId === 'all'"
            @click="openExceptionModal()"
          >
            {{ t('WORKFORCE.EXCEPTIONS.ACTIONS.ADD') }}
          </WorkforceButton>
        </div>
        <div class="px-5 py-4">
          <p class="text-xs text-n-slate-9">
            {{ t('WORKFORCE.EXCEPTIONS.DESCRIPTION') }}
          </p>
          <p class="mt-1 text-[11px] text-n-slate-9">
            {{ t('WORKFORCE.EXCEPTIONS.DESCRIPTION_DETAIL') }}
          </p>
          <p v-if="filters.userId === 'all'" class="text-sm text-n-slate-10">
            {{ t('WORKFORCE.EXCEPTIONS.SELECT_AGENT_HINT') }}
          </p>
          <p v-else-if="exceptionLoading" class="text-sm text-n-slate-10">
            {{ t('WORKFORCE.EXCEPTIONS.LOADING') }}
          </p>
          <p v-else-if="!exceptionList.length" class="text-sm text-n-slate-10">
            {{ t('WORKFORCE.EXCEPTIONS.EMPTY') }}
          </p>
          <ul v-else class="grid gap-3">
            <li
              v-for="item in exceptionList"
              :key="item.id"
              class="rounded-lg border border-n-weak bg-n-solid-3 px-4 py-3"
            >
              <div class="flex items-center justify-between gap-3">
                <div>
                  <p class="text-sm font-semibold text-n-slate-12">
                    {{ item.dateLabel }}
                  </p>
                  <p class="text-xs text-n-slate-10">
                    {{ item.timeLabel }}
                  </p>
                  <p v-if="item.reason" class="text-xs text-n-slate-10">
                    {{ item.reason }}
                  </p>
                </div>
                <div class="flex gap-2">
                  <WorkforceButton
                    variant="ghost"
                    size="xs"
                    icon="i-lucide-pencil"
                    @click="openExceptionModal(item)"
                  >
                    {{ t('WORKFORCE.ACTIONS.EDIT') }}
                  </WorkforceButton>
                  <WorkforceButton
                    variant="ghost"
                    size="xs"
                    icon="i-lucide-trash"
                    @click="deleteException(item)"
                  >
                    {{ t('WORKFORCE.ACTIONS.DELETE') }}
                  </WorkforceButton>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <WorkforceModal
      v-model="colorDesignerModal.open"
      :title="t('WORKFORCE.SCHEDULES.COLOR_MODAL.TITLE')"
      :description="t('WORKFORCE.SCHEDULES.COLOR_MODAL.DESCRIPTION')"
      :confirm-label="t('WORKFORCE.ACTIONS.SAVE')"
      :cancel-label="t('WORKFORCE.ACTIONS.CANCEL')"
      width="lg"
      @confirm="submitColorDesigner"
      @cancel="closeColorDesigner"
    >
      <div class="grid gap-4">
        <p v-if="!agents.length" class="text-sm text-n-slate-10">
          {{ t('WORKFORCE.SCHEDULES.COLOR_MODAL.EMPTY') }}
        </p>
        <template v-else>
          <div
            v-for="agent in agents"
            :key="agent.id"
            class="flex flex-col gap-2 rounded-lg border border-n-weak bg-n-solid-2 px-4 py-3"
          >
            <div class="flex items-center justify-between gap-2">
              <span class="text-sm font-semibold text-n-slate-12">{{
                agent.name
              }}</span>
              <span
                class="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium"
                :class="[
                  agentColorMap[Number(agent.id)]?.bgClass || 'bg-black',
                  agentColorMap[Number(agent.id)]?.textClass || 'text-white',
                ]"
                :style="
                  agentColorMap[Number(agent.id)]
                    ? {
                        backgroundColor: agentColorMap[Number(agent.id)].bgHex,
                        color: agentColorMap[Number(agent.id)].textHex,
                      }
                    : null
                "
              >
                {{
                  agentColorMap[Number(agent.id)]?.label ||
                  t('WORKFORCE.SCHEDULES.COLOR_MODAL.DEFAULT')
                }}
              </span>
            </div>
            <WorkforceSelect
              v-model="colorDesignerModal.selections[agent.id]"
              class="mt-1"
            >
              <option
                v-for="option in colorOptions"
                :key="option.id"
                :value="option.id"
              >
                {{ option.label }}
              </option>
            </WorkforceSelect>
          </div>
        </template>
        <div v-if="agents.length" class="flex justify-end">
          <WorkforceButton
            variant="ghost"
            size="xs"
            icon="i-lucide-rotate-ccw"
            @click="resetColorDesignerSelections"
          >
            {{ t('WORKFORCE.SCHEDULES.COLOR_MODAL.RESET') }}
          </WorkforceButton>
        </div>
      </div>
    </WorkforceModal>

    <WorkforceModal
      v-model="weeklyPatternModal"
      :title="t('WORKFORCE.SCHEDULES.BULK_WEEK.TITLE')"
      :description="t('WORKFORCE.SCHEDULES.BULK_WEEK.DESCRIPTION')"
      hide-footer
      width="full"
      :close-on-outside="false"
      @cancel="closeWeeklyPatternModal"
    >
      <div class="flex justify-between items-center mb-4">
        <p class="text-xs text-n-slate-10">
          {{
            t('WORKFORCE.SCHEDULES.BULK_WEEK.TEMPLATE_COUNT', {
              count: weeklyPatternTemplate.schedules.length,
            })
          }}
        </p>
        <WorkforceButton
          variant="solid"
          size="sm"
          color="slate"
          icon="i-lucide-x"
          type="button"
          @click="closeWeeklyPatternModal"
        >
          {{ t('WORKFORCE.ACTIONS.CLOSE') }}
        </WorkforceButton>
      </div>
      <p class="text-[11px] text-n-slate-9">
        {{ t('WORKFORCE.SCHEDULES.BULK_WEEK.MODAL_HINT') }}
      </p>
      <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div
          v-for="day in weeklyPatternTemplateDays"
          :key="day.index"
          class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-3 p-4 shadow-sm"
        >
          <div class="flex items-center justify-between gap-2">
            <div>
              <p class="text-xs uppercase tracking-wide text-n-slate-10">
                {{ day.label }}
              </p>
              <p class="text-sm font-semibold text-n-slate-12">
                {{ day.dateLabel }}
              </p>
            </div>
            <div class="flex flex-wrap justify-end gap-2">
              <WorkforceButton
                size="xs"
                variant="ghost"
                color="onyx"
                icon="i-lucide-plus"
                type="button"
                @click="openTemplateScheduleModal(day.index)"
              >
                {{ t('WORKFORCE.SCHEDULES.WEEKLY.ADD_SLOT') }}
              </WorkforceButton>
              <WorkforceButton
                size="xs"
                variant="ghost"
                color="onyx"
                icon="i-lucide-users"
                type="button"
                @click="openTemplateDayCoverageModal(day.index, day.isoDate)"
              >
                {{ t('WORKFORCE.SCHEDULES.WEEKLY.ASSIGN_DAY') }}
              </WorkforceButton>
            </div>
          </div>
          <div class="grid gap-2">
            <p v-if="!day.schedules.length" class="text-xs text-n-slate-9">
              {{ t('WORKFORCE.SCHEDULES.WEEKLY.EMPTY') }}
            </p>
            <ul v-else class="grid gap-2">
              <li
                v-for="schedule in day.schedules"
                :key="schedule.id"
                class="group flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-xs transition hover:border-n-strong hover:bg-n-solid-1"
                @click="openTemplateScheduleModal(day.index, schedule.raw)"
              >
                <div class="flex items-center gap-2">
                  <span
                    class="inline-flex h-2.5 w-2.5 rounded-full"
                    :class="schedule.color?.chipClass || 'bg-black'"
                    :style="
                      schedule.color
                        ? { backgroundColor: schedule.color.bgHex }
                        : null
                    "
                  />
                  <div class="flex flex-col">
                    <span class="font-semibold">
                      {{
                        schedule.agentName ||
                        t('WORKFORCE.LABELS.UNKNOWN_AGENT')
                      }}
                    </span>
                    <span class="text-[11px] text-n-slate-9">
                      {{ schedule.startLabel }} – {{ schedule.endLabel }}
                    </span>
                  </div>
                </div>
                <div class="flex items-center gap-2">
                  <span
                    v-if="!schedule.isActive"
                    class="rounded-full bg-amber-3/50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-11"
                  >
                    {{ t('WORKFORCE.SCHEDULES.TABLE.STATUS_INACTIVE') }}
                  </span>
                  <WorkforceButton
                    size="xs"
                    variant="ghost"
                    color="onyx"
                    icon="i-lucide-edit"
                    type="button"
                    @click.stop="
                      openTemplateScheduleModal(day.index, schedule.raw)
                    "
                  />
                  <WorkforceButton
                    size="xs"
                    variant="ghost"
                    color="onyx"
                    icon="i-lucide-trash-2"
                    type="button"
                    @click.stop="deleteTemplateSchedule(schedule.raw)"
                  />
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </WorkforceModal>

    <WorkforceModal
      v-model="dayCoverageModal.open"
      :title="
        t('WORKFORCE.SCHEDULES.BULK_DAY.TITLE', {
          date: dayCoverageModal.form.dateLabel,
        })
      "
      :description="t('WORKFORCE.SCHEDULES.BULK_DAY.DESCRIPTION')"
      :confirm-label="t('WORKFORCE.ACTIONS.SAVE')"
      :cancel-label="t('WORKFORCE.ACTIONS.CANCEL')"
      :loading="dayCoverageModal.loading"
      width="xl"
      @confirm="submitDayCoverageForm"
      @cancel="closeDayCoverageModal"
    >
      <div class="grid gap-4">
        <div class="grid gap-4 md:grid-cols-2">
          <label
            class="grid gap-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
          >
            {{ t('WORKFORCE.FIELDS.WEEKDAY') }}
            <WorkforceSelect
              v-model="dayCoverageModal.form.weekday"
              class="mt-1"
            >
              <option
                v-for="(key, index) in weekdayKeys"
                :key="key"
                :value="index"
              >
                {{ t(`WORKFORCE.WEEKDAYS.${key}`) }}
              </option>
            </WorkforceSelect>
          </label>
          <label
            class="grid gap-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
          >
            {{ t('WORKFORCE.SCHEDULES.BULK_DAY.SELECTED_DATE') }}
            <input
              v-model="dayCoverageModal.form.date"
              type="date"
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
            />
          </label>
        </div>
        <div class="grid gap-4 md:grid-cols-2">
          <label
            class="grid gap-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
          >
            {{ t('WORKFORCE.FIELDS.START_TIME') }}
            <input
              v-model="dayCoverageModal.form.start_time"
              type="time"
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
            />
          </label>
          <label
            class="grid gap-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
          >
            {{ t('WORKFORCE.FIELDS.END_TIME') }}
            <input
              v-model="dayCoverageModal.form.end_time"
              type="time"
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
            />
          </label>
        </div>
        <div>
          <p
            class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
          >
            {{ t('WORKFORCE.SCHEDULES.BULK_DAY.AGENTS_LABEL') }}
          </p>
          <div class="mt-2 max-h-56 space-y-2 overflow-y-auto pr-2">
            <label
              v-for="agent in agents"
              :key="agent.id"
              class="flex cursor-pointer items-center justify-between gap-2 rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 transition hover:border-n-strong hover:bg-n-solid-1"
            >
              <span class="font-semibold">{{ agent.name }}</span>
              <input
                v-model="dayCoverageModal.form.agentIds"
                class="size-4 rounded border-n-slate-400 text-black focus:ring-n-slate-500/40 dark:border-n-slate-600 dark:text-white dark:focus:ring-white/40"
                type="checkbox"
                :value="String(agent.id)"
              />
            </label>
          </div>
        </div>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input
            v-model="dayCoverageModal.form.replaceExisting"
            type="checkbox"
            class="size-4 rounded border-n-slate-400 text-black focus:ring-n-slate-500/40 dark:border-n-slate-600 dark:text-white dark:focus:ring-white/40"
          />
          {{ t('WORKFORCE.SCHEDULES.BULK_DAY.REPLACE_HINT') }}
        </label>
      </div>
    </WorkforceModal>

    <WorkforceModal
      v-model="scheduleModal.open"
      :title="
        scheduleModal.isNew
          ? t('WORKFORCE.FORMS.CREATE_SCHEDULE_TITLE')
          : t('WORKFORCE.FORMS.EDIT_SCHEDULE_TITLE')
      "
      :confirm-label="t('WORKFORCE.ACTIONS.SAVE')"
      :cancel-label="t('WORKFORCE.ACTIONS.CANCEL')"
      :loading="savingSchedule"
      width="lg"
      @confirm="submitSchedule"
      @cancel="resetScheduleForm"
    >
      <div class="grid gap-4 md:grid-cols-2">
        <label
          class="grid gap-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.FIELDS.AGENT') }}
          <WorkforceSelect v-model="scheduleModal.form.user_id" class="mt-1">
            <option value="" disabled>
              {{ t('WORKFORCE.FIELDS.AGENT_PLACEHOLDER') }}
            </option>
            <option
              v-for="agent in agents"
              :key="agent.id"
              :value="Number(agent.id)"
            >
              {{ agent.name }}
            </option>
          </WorkforceSelect>
        </label>
        <label
          class="grid gap-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.FIELDS.WEEKDAY') }}
          <WorkforceSelect v-model="scheduleModal.form.weekday" class="mt-1">
            <option
              v-for="(key, index) in weekdayKeys"
              :key="key"
              :value="index"
            >
              {{ t(`WORKFORCE.WEEKDAYS.${key}`) }}
            </option>
          </WorkforceSelect>
        </label>
        <label
          class="grid gap-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.FIELDS.START_TIME') }}
          <input
            v-model="scheduleModal.form.start_time"
            type="time"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
          />
        </label>
        <label
          class="grid gap-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.FIELDS.END_TIME') }}
          <input
            v-model="scheduleModal.form.end_time"
            type="time"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
          />
        </label>
        <label
          class="grid gap-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.FIELDS.TIMEZONE') }}
          <input
            v-model="scheduleModal.form.timezone"
            type="text"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
          />
        </label>
        <label
          class="grid gap-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.FIELDS.BUFFER') }}
          <input
            v-model.number="scheduleModal.form.buffer_minutes"
            type="number"
            min="0"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
          />
        </label>
        <label
          class="flex items-center gap-2 text-sm text-n-slate-12 md:col-span-2"
        >
          <input
            v-model="scheduleModal.form.is_active"
            type="checkbox"
            class="size-4 rounded border-n-slate-400 text-black focus:ring-n-slate-500/40 dark:border-n-slate-600 dark:text-white dark:focus:ring-white/40"
          />
          {{ t('WORKFORCE.FIELDS.ACTIVE') }}
        </label>
      </div>
    </WorkforceModal>

    <WorkforceModal
      v-model="exceptionModal.open"
      :title="
        exceptionModal.isNew
          ? t('WORKFORCE.EXCEPTIONS.FORM.CREATE_TITLE')
          : t('WORKFORCE.EXCEPTIONS.FORM.EDIT_TITLE')
      "
      :confirm-label="t('WORKFORCE.ACTIONS.SAVE')"
      :cancel-label="t('WORKFORCE.ACTIONS.CANCEL')"
      :loading="exceptionSaving"
      width="lg"
      @confirm="submitExceptionForm"
      @cancel="resetExceptionForm"
    >
      <div class="grid gap-4 md:grid-cols-2">
        <label
          class="grid gap-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.FIELDS.AGENT') }}
          <WorkforceSelect v-model="exceptionModal.form.user_id" class="mt-1">
            <option value="" disabled>
              {{ t('WORKFORCE.FIELDS.AGENT_PLACEHOLDER') }}
            </option>
            <option
              v-for="agent in agents"
              :key="agent.id"
              :value="String(agent.id)"
            >
              {{ agent.name }}
            </option>
          </WorkforceSelect>
        </label>
        <label
          class="grid gap-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.EXCEPTIONS.FORM.DATE') }}
          <input
            v-model="exceptionModal.form.date"
            type="date"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
          />
        </label>
        <label
          class="grid gap-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.EXCEPTIONS.FORM.STATUS') }}
          <WorkforceSelect v-model="exceptionModal.form.status" class="mt-1">
            <option value="time_off">
              {{ t('WORKFORCE.EXCEPTIONS.STATUS.TIME_OFF') }}
            </option>
            <option value="overtime">
              {{ t('WORKFORCE.EXCEPTIONS.STATUS.OVERTIME') }}
            </option>
            <option value="override">
              {{ t('WORKFORCE.EXCEPTIONS.STATUS.OVERRIDE') }}
            </option>
            <option value="custom">
              {{ t('WORKFORCE.EXCEPTIONS.STATUS.CUSTOM') }}
            </option>
          </WorkforceSelect>
        </label>
        <label
          class="grid gap-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.FIELDS.START_TIME') }}
          <input
            v-model="exceptionModal.form.start_time"
            type="time"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
          />
        </label>
        <label
          class="grid gap-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.FIELDS.END_TIME') }}
          <input
            v-model="exceptionModal.form.end_time"
            type="time"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
          />
          <span
            v-if="exceptionModal.form.reason_error"
            class="text-[11px] text-rose-11"
          >
            {{ exceptionModal.form.reason_error }}
          </span>
        </label>
        <label
          class="grid gap-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.FIELDS.TIMEZONE') }}
          <input
            v-model="exceptionModal.form.timezone"
            type="text"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
          />
        </label>
        <label class="md:col-span-2 lg:col-span-3">
          <span
            class="block text-[11px] font-medium uppercase tracking-wide text-n-slate-10"
          >
            {{ t('WORKFORCE.EXCEPTIONS.FORM.REASON') }}
          </span>
          <textarea
            v-model="exceptionModal.form.reason"
            rows="2"
            class="mt-1 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
          />
        </label>
      </div>
    </WorkforceModal>

    <WorkforceMonthlyCalendarModal
      v-model="monthlyViewModal"
      :schedules="calendarSchedules"
      :exceptions="scheduleExceptions"
      :agents="agents"
      :agent-colors="agentColorMap"
      :replacing-week-key="replacingWeekKey"
      @edit:schedule="openScheduleModal"
      @edit:exception="handleEditException"
      @create:schedule="handleCalendarCreate"
      @assign:coverage="handleMonthlyAssignCoverage"
      @replace:week="handleReplaceWeek"
    />
  </section>
</template>
